import { Avatar, Badge, DateText, Input, Tabs } from '@/components';
import { cn } from '@/lib/cn';
import { For, type JSX, Show, createMemo, createSignal } from 'solid-js';
import type { PollAnswerValue, PollFieldResult, PollResults } from './api';

type Respondent = NonNullable<PollResults['respondents']>[number];
type User = Respondent['user'];

const CHOICE_TYPES = new Set(['single_choice', 'multi_choice', 'dropdown']);
const NUMBER_TYPES = new Set(['number', 'scale']);
const STACK = 4;
const TEXT_PREVIEW = 3;
const CARD =
  'rounded-xl border border-neutral-200 bg-surface shadow-sm dark:border-neutral-800 dark:bg-surface-dark';

const displayValue = (value: PollAnswerValue): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return `${value.start} – ${value.end}`;
  return String(value);
};
const name = (user: User) => user.displayName || user.email;
const answerOf = (respondent: Respondent, fieldId: string) =>
  respondent.answers.find((answer) => answer.fieldId === fieldId)?.value;
const picks = (value: PollAnswerValue | undefined): readonly string[] =>
  typeof value === 'string' ? [value] : Array.isArray(value) ? value : [];

const Chevron = (props: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    class={cn(
      'shrink-0 text-neutral-400 transition-transform duration-200 motion-reduce:transition-none',
      props.open && 'rotate-180',
    )}
  >
    <path d="M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4z" />
  </svg>
);

/** Height-animated disclosure body (grid-rows 0fr → 1fr). */
const Reveal = (props: { open: boolean; children: JSX.Element }) => (
  <div
    class={cn(
      'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
      props.open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
    )}
  >
    {/* Collapsed content stays out of the tab order and the accessibility tree. */}
    <div class="overflow-hidden" inert={!props.open || undefined} aria-hidden={!props.open}>
      {props.children}
    </div>
  </div>
);

const ChoiceOption = (props: {
  label: string;
  count: number;
  answered: number;
  max: number;
  voters: readonly User[] | null;
}) => {
  const [open, setOpen] = createSignal(false);
  const top = () => props.count > 0 && props.count === props.max;
  const canOpen = () => Boolean(props.voters?.length);
  return (
    <div>
      <button
        type="button"
        disabled={!canOpen()}
        aria-expanded={canOpen() ? open() : undefined}
        onClick={() => setOpen(!open())}
        class={cn(
          'grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 rounded-lg px-3 py-2.5 text-left transition-colors duration-200 motion-reduce:transition-none',
          canOpen() && 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60',
          open() && 'bg-neutral-50 dark:bg-neutral-800/60',
        )}
      >
        <span class="flex min-w-0 items-center gap-2 text-sm font-medium">
          <span class="break-words">{props.label}</span>
          <Show when={top()}>
            <Badge tone="success" class="px-1.5 py-0 text-[11px]">
              Top
            </Badge>
          </Show>
        </span>
        <span class="flex items-center gap-2.5 text-sm tabular-nums text-neutral-500">
          <Show when={props.voters?.length}>
            <span class="hidden -space-x-1.5 sm:flex" aria-hidden="true">
              <For each={props.voters?.slice(0, STACK)}>
                {(user) => (
                  <Avatar
                    user={user}
                    class="size-[22px] text-[9px] ring-2 ring-white dark:ring-surface-dark"
                  />
                )}
              </For>
              <Show when={(props.voters?.length ?? 0) > STACK}>
                <span class="flex size-[22px] items-center justify-center rounded-full bg-neutral-100 text-[9px] font-semibold ring-2 ring-white dark:bg-neutral-800 dark:ring-surface-dark">
                  +{(props.voters?.length ?? 0) - STACK}
                </span>
              </Show>
            </span>
          </Show>
          <span>
            <b class="text-neutral-900 dark:text-neutral-100">{props.count}</b> ·{' '}
            {props.answered ? Math.round((props.count / props.answered) * 100) : 0}%
          </span>
          <Show when={canOpen()}>
            <Chevron open={open()} />
          </Show>
        </span>
        <span class="col-span-2 h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
          <span
            class={cn(
              'block h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none',
              top() ? 'bg-brand-600' : 'bg-brand-400',
            )}
            style={`width:${(props.count / Math.max(1, props.max)) * 100}%`}
          />
        </span>
      </button>
      <Show when={props.voters}>
        {(voters) => (
          <Reveal open={open()}>
            <div class="flex flex-wrap gap-1.5 px-3 pb-3 pt-2">
              <For each={voters()}>
                {(user) => (
                  <span class="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 py-0.5 pl-0.5 pr-2.5 text-xs dark:border-neutral-700 dark:bg-neutral-800">
                    <Avatar user={user} class="size-5 text-[9px]" />
                    {name(user)}
                  </span>
                )}
              </For>
            </div>
          </Reveal>
        )}
      </Show>
    </div>
  );
};

const ResultCard = (props: {
  field: PollFieldResult;
  index: number;
  total: number;
  respondents: PollResults['respondents'];
}) => {
  const maxCount = () => Math.max(0, ...props.field.options.map((option) => option.count));
  const votersFor = (optionId: string) =>
    props.respondents
      ?.filter((r) => picks(answerOf(r, props.field.fieldId)).includes(optionId))
      .map((r) => r.user) ?? null;
  const distribution = () =>
    [...props.field.distribution].sort((a, b) => Number(a.value) - Number(b.value));
  const maxBucket = () => Math.max(0, ...props.field.distribution.map((item) => item.count));
  const votersOf = (value: string) =>
    props.respondents
      ?.filter((r) => String(answerOf(r, props.field.fieldId)) === value)
      .map((r) => r.user) ?? null;
  const texts = () =>
    props.respondents
      ? props.respondents.flatMap((r) => {
          const value = answerOf(r, props.field.fieldId);
          return value === undefined ? [] : [{ value, user: r.user, at: r.submittedAt }];
        })
      : props.field.values.map((value) => ({ value, user: null, at: null }));
  const [showAll, setShowAll] = createSignal(false);
  const skipped = () => props.total - props.field.responseCount;

  return (
    <section class={cn(CARD, 'p-5')}>
      <div class="mb-4 flex items-start justify-between gap-3">
        <div class="flex gap-2.5">
          <span class="flex size-6 shrink-0 items-center justify-center rounded-md bg-brand-50 text-xs font-semibold text-brand-600 dark:bg-brand-900/40 dark:text-brand-400">
            {props.index}
          </span>
          <div>
            <h2 class="font-semibold">{props.field.label}</h2>
            <p class="text-xs text-neutral-500">
              {props.field.responseCount} of {props.total} answered
              <Show when={skipped() > 0}> · {skipped()} skipped</Show>
            </p>
          </div>
        </div>
        <Show
          when={
            props.respondents &&
            (CHOICE_TYPES.has(props.field.type) || NUMBER_TYPES.has(props.field.type))
          }
        >
          <Badge tone="info" class="hidden shrink-0 sm:inline-flex">
            Click an option to see who
          </Badge>
        </Show>
      </div>

      <Show when={CHOICE_TYPES.has(props.field.type)}>
        <div class="flex flex-col gap-1">
          <For each={props.field.options}>
            {(option) => (
              <ChoiceOption
                label={option.label}
                count={option.count}
                answered={props.field.responseCount}
                max={maxCount()}
                voters={votersFor(option.id)}
              />
            )}
          </For>
        </div>
        <Show when={props.field.type === 'multi_choice'}>
          <p class="mt-2.5 text-xs text-neutral-500">
            People could pick more than one · % of {props.field.responseCount} who answered
          </p>
        </Show>
      </Show>

      <Show when={NUMBER_TYPES.has(props.field.type)}>
        <Show when={props.field.average !== null}>
          <p class="mb-3 flex items-baseline gap-1.5 text-sm text-neutral-500">
            <span class="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
              Average {props.field.average?.toFixed(2)}
            </span>
          </p>
        </Show>
        <div class="flex flex-col gap-1">
          <For each={distribution()}>
            {(item) => (
              <ChoiceOption
                label={item.value}
                count={item.count}
                answered={props.field.responseCount}
                max={maxBucket()}
                voters={votersOf(item.value)}
              />
            )}
          </For>
        </div>
      </Show>

      <Show
        when={
          !CHOICE_TYPES.has(props.field.type) &&
          !NUMBER_TYPES.has(props.field.type) &&
          props.field.type !== 'section'
        }
      >
        <Show
          when={texts().length}
          fallback={<p class="text-sm italic text-neutral-500">No answers yet</p>}
        >
          <ul class="flex flex-col gap-2 text-sm">
            <For each={showAll() ? texts() : texts().slice(0, TEXT_PREVIEW)}>
              {(item) => (
                <li class="flex gap-2.5 rounded-lg bg-neutral-50 px-3 py-2.5 dark:bg-neutral-900">
                  <Show when={item.user}>{(user) => <Avatar user={user()} class="size-7" />}</Show>
                  <div class="min-w-0">
                    <p class="whitespace-pre-wrap break-words">{displayValue(item.value)}</p>
                    <Show when={item.user}>
                      {(user) => (
                        <p class="text-xs text-neutral-500">
                          {name(user())} · <DateText value={item.at} />
                        </p>
                      )}
                    </Show>
                  </div>
                </li>
              )}
            </For>
          </ul>
          <Show when={texts().length > TEXT_PREVIEW}>
            <button
              type="button"
              class="mt-2 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
              onClick={() => setShowAll(!showAll())}
            >
              {showAll() ? 'Show less' : `Show all ${texts().length} answers`}
            </button>
          </Show>
        </Show>
      </Show>
    </section>
  );
};

const PersonCard = (props: { respondent: Respondent; fields: readonly PollFieldResult[] }) => {
  const [open, setOpen] = createSignal(false);
  const answer = (field: PollFieldResult): JSX.Element => {
    const value = answerOf(props.respondent, field.fieldId);
    if (value === undefined || value === '')
      return <span class="italic text-neutral-500">Skipped</span>;
    if (!CHOICE_TYPES.has(field.type)) return displayValue(value);
    return (
      <For each={picks(value)}>
        {(id) => (
          <span class="rounded-md bg-brand-50 px-2 py-px font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
            {field.options.find((option) => option.id === id)?.label ?? id}
          </span>
        )}
      </For>
    );
  };
  return (
    <div class={cn(CARD, 'overflow-hidden')}>
      <button
        type="button"
        aria-expanded={open()}
        onClick={() => setOpen(!open())}
        class="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors duration-200 hover:bg-neutral-50 motion-reduce:transition-none dark:hover:bg-neutral-800/60"
      >
        <Avatar user={props.respondent.user} class="size-8" />
        <span class="min-w-0">
          <span class="block truncate font-semibold">{name(props.respondent.user)}</span>
          <span class="block truncate text-xs text-neutral-500">{props.respondent.user.email}</span>
        </span>
        <span class="ml-auto whitespace-nowrap text-xs text-neutral-500">
          <DateText value={props.respondent.submittedAt} />
        </span>
        <Chevron open={open()} />
      </button>
      <Reveal open={open()}>
        <dl class="grid gap-x-4 gap-y-2 border-t border-neutral-200 px-4 py-3.5 text-sm sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:pl-15 dark:border-neutral-800">
          <For each={props.fields}>
            {(field) => (
              <>
                <dt class="text-neutral-500">{field.label}</dt>
                <dd class="flex flex-wrap gap-1">{answer(field)}</dd>
              </>
            )}
          </For>
        </dl>
      </Reveal>
    </div>
  );
};

const People = (props: {
  respondents: readonly Respondent[];
  fields: readonly PollFieldResult[];
}) => {
  const [search, setSearch] = createSignal('');
  const shown = createMemo(() => {
    const q = search().trim().toLowerCase();
    return q
      ? props.respondents.filter((r) =>
          `${r.user.displayName} ${r.user.email}`.toLowerCase().includes(q),
        )
      : props.respondents;
  });
  return (
    <div class="flex flex-col gap-3">
      <Input
        type="search"
        aria-label="Search people"
        placeholder="Search people"
        value={search()}
        onInput={(e) => setSearch(e.currentTarget.value)}
        class="sm:max-w-xs"
      />
      <Show
        when={shown().length}
        fallback={
          <p class={cn(CARD, 'p-8 text-center text-sm text-neutral-500')}>No one matches</p>
        }
      >
        <For each={shown()}>
          {(respondent) => <PersonCard respondent={respondent} fields={props.fields} />}
        </For>
      </Show>
    </div>
  );
};

export const ResultsDashboard = (props: { results: PollResults }) => {
  const [tab, setTab] = createSignal('summary');
  const answerable = () => props.results.fields.filter((field) => field.type !== 'section');
  const latest = () =>
    props.results.respondents?.reduce<string | null>(
      (last, r) => (!last || r.submittedAt > last ? r.submittedAt : last),
      null,
    ) ?? null;
  const questionNumber = (field: PollFieldResult) => answerable().indexOf(field) + 1;

  return (
    <div class="flex flex-col gap-5">
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div class={cn(CARD, 'p-4')}>
          <p class="text-xs text-neutral-500">Responses</p>
          <p class="mt-0.5 text-2xl font-semibold tabular-nums">{props.results.responseCount}</p>
        </div>
        <div class={cn(CARD, 'p-4')}>
          <p class="text-xs text-neutral-500">Latest response</p>
          <p class="mt-1.5 font-semibold">
            <DateText value={latest()} empty={props.results.poll.isAnonymous ? 'Hidden' : '—'} />
          </p>
        </div>
        <div class={cn(CARD, 'p-4')}>
          <p class="text-xs text-neutral-500">Closes</p>
          <p class="mt-1.5 font-semibold">
            <DateText value={props.results.poll.closesAt} empty="No deadline" />
          </p>
        </div>
        <div class={cn(CARD, 'p-4')}>
          <p class="text-xs text-neutral-500">Privacy</p>
          <p class="mt-1.5 font-semibold">
            {props.results.poll.isAnonymous ? '🔒 Anonymous' : '👤 Names visible'}
          </p>
        </div>
      </div>

      <Show
        when={props.results.responseCount > 0}
        fallback={
          <div class={cn(CARD, 'px-4 py-12 text-center')}>
            <p class="text-3xl" aria-hidden="true">
              🗳️
            </p>
            <p class="mt-2 font-semibold">No responses yet</p>
            <p class="text-sm text-neutral-500">Share the poll link to start collecting answers.</p>
          </div>
        }
      >
        <Tabs
          tabs={[
            { id: 'summary', label: 'Summary' },
            { id: 'people', label: 'By person', count: props.results.respondents?.length },
          ]}
          active={tab()}
          onChange={setTab}
        />
        <Show
          when={tab() === 'people'}
          fallback={
            <div class="flex flex-col gap-4">
              <For each={props.results.fields}>
                {(field) =>
                  field.type === 'section' ? (
                    <h2 class="border-b border-neutral-200 pb-2 pt-3 text-lg font-semibold dark:border-neutral-800">
                      {field.label}
                    </h2>
                  ) : (
                    <ResultCard
                      field={field}
                      index={questionNumber(field)}
                      total={props.results.responseCount}
                      respondents={props.results.respondents}
                    />
                  )
                }
              </For>
            </div>
          }
        >
          <Show
            when={props.results.respondents}
            fallback={
              <p class="rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                🔒 This poll is anonymous — individual answers are hidden. Use the Summary tab for
                totals.
              </p>
            }
          >
            {(respondents) => <People respondents={respondents()} fields={answerable()} />}
          </Show>
        </Show>
      </Show>
    </div>
  );
};
